/**
 * Callable routing: which deployed function serves a callable.
 *
 * Many callables are served by a BUNDLE function (e.g. `admin-org`) instead of
 * their own: the bundle is called with `{ op, data }` (op = the old callable name) and runs
 * that op's handler unchanged. `callable-routing.json` is GENERATED from the
 * functions repo (`functions/src/bundles.ts`, `npm run routing:export`); never
 * edit it by hand. A name not in the table is still its own function, so it is
 * called directly, exactly as before.
 */
import { httpsCallable, type Functions, type HttpsCallableResult } from 'firebase/functions';
import routing from './callable-routing.json';

const ROUTES: Readonly<Record<string, string>> = routing.routes;

export type CallableRoute = {
  /** The deployed function to call. */
  target: string;
  /** What to send it. */
  payload: unknown;
};

export function routeCallable(name: string, data: unknown): CallableRoute {
  const bundle = Object.prototype.hasOwnProperty.call(ROUTES, name) ? ROUTES[name] : undefined;
  return bundle ?
    { target: bundle, payload: { op: name, data: data ?? null } } :
    { target: name, payload: data };
}

/**
 * Drop-in for `httpsCallable(functions, name)`: the same call shape, but routed
 * through the callable's bundle when it has one. Use this (or the app's call
 * wrapper) for every callable, never `httpsCallable` directly.
 */
export function routedHttpsCallable<Req = unknown, Res = unknown>(
  functions: Functions,
  name: string,
): (data?: Req | null) => Promise<HttpsCallableResult<Res>> {
  return (data) => {
    const { target, payload } = routeCallable(name, data);
    return httpsCallable<unknown, Res>(functions, target)(payload);
  };
}
